import {
  Design,
  Areas,
  Flow,
  FlowStop,
  Group,
  InterAreaConnection,
  JustInTimeRoutingConfig,
  LoadType,
  LogicalSpace,
  Obstacle,
  VehicleType,
  Floorplan,
  Track,
  TrafficRulesCatalog,
  MetaData,
  FlowLayer,
  Templates,
  FloorplanRevision,
  ReferenceImage,
  UxOnly,
  FileReference,
} from '@thive/fddm-types';

import { Area, Template } from '@/modules/floorplanDesign/types';
import { FddmPreferenceValues } from '@/modules/floorplanDesign/preferences';
import { IProjectServiceConnector } from '@/modules/api/ProjectApiConnector';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { useCanvasStore } from '@/modules/canvas/store';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { replaceURLPath } from '@/modules/app/helpers';
import { buildTagMatchRegExp } from '@/modules/floorplanDesign/helpers';
import { LayerNames } from '@/modules/common/types/layers';

import { IFddmService } from './IFddmService';
import { logger } from '@common/logging/logger';

const THUMBNAIL_CONFIG = {
  WIDTH: 540,
  HEIGHT: 360,
} as const;

type AutosaveContext = {
  projectId: string;
  alternativeId: string;
  revisionId: string;
  revisionState: MetaData['state'];
};

export class FddmServiceAutosaveDecorator implements IFddmService {
  private decoree: IFddmService;
  private appStore: typeof useAppStore;
  private appMetaStore: typeof useAppMetaStore;
  private canvasStore: typeof useCanvasStore;
  private fddmStore: typeof useFloorplanDesignStore;
  private projectServiceConnector: IProjectServiceConnector;
  private autosaveQueued = false;
  private autosaveInProgress = false;

  constructor(
    decoree: IFddmService,
    appStore: typeof useAppStore,
    appMetaStore: typeof useAppMetaStore,
    canvasStore: typeof useCanvasStore,
    fddmStore: typeof useFloorplanDesignStore,
    projectServiceConnector: IProjectServiceConnector,
  ) {
    this.decoree = decoree;
    this.appStore = appStore;
    this.appMetaStore = appMetaStore;
    this.canvasStore = canvasStore;
    this.fddmStore = fddmStore;
    this.projectServiceConnector = projectServiceConnector;
  }

  initialize(revision: FloorplanRevision): void {
    this.decoree.initialize(revision);
  }

  async regenerateAll(): Promise<void> {
    await this.decoree.regenerateAll();
  }

  getModel(): FloorplanRevision {
    return this.decoree.getModel();
  }

  // MetaData

  getMetaData(): MetaData {
    return this.decoree.getMetaData();
  }

  setMetaData(metaData: MetaData): void {
    this.decoree.setMetaData(metaData);
    this.triggerAutosave();
  }

  //
  // Reference image
  //

  getReferenceImage(): UxOnly['referenceImage'] {
    return this.decoree.getReferenceImage();
  }

  addReferenceImage(referenceImage: ReferenceImage): void {
    this.decoree.addReferenceImage(referenceImage);
    this.triggerAutosave();
  }

  updateReferenceImage(referenceImage: ReferenceImage): void {
    this.decoree.updateReferenceImage(referenceImage);
    this.triggerAutosave();
  }

  deleteReferenceImage(): void {
    this.decoree.deleteReferenceImage();
    this.triggerAutosave();
  }

  //
  // Point cloud
  //

  addPointCloudInputFileRef(fileRef: string): void {
    this.decoree.addPointCloudInputFileRef(fileRef);
    this.triggerAutosave();
  }

  addPointCloudOutputFileRef(fileRef: string): void {
    this.decoree.addPointCloudOutputFileRef(fileRef);
    this.triggerAutosave();
  }

  deletePointCloudInputFileRef(): void {
    this.decoree.deletePointCloudInputFileRef();
    this.triggerAutosave();
  }

  deletePointCloudOutputFileRef(): void {
    this.decoree.deletePointCloudOutputFileRef();
    this.triggerAutosave();
  }

  //
  // Design
  //

  getDesign(): Design {
    return this.decoree.getDesign();
  }

  setDesign(design: Design): void {
    this.decoree.setDesign(design);
    this.triggerAutosave();
  }

  //
  // Areas
  //

  getArea(id: string): Area | undefined {
    return this.decoree.getArea(id);
  }

  async addAreas(areas: Areas): Promise<void> {
    await this.decoree.addAreas(areas);
    this.triggerAutosave();
  }

  updateArea(area: Area): void {
    this.decoree.updateArea(area);
    this.triggerAutosave();
  }

  updateAreas(areas: Areas): void {
    this.decoree.updateAreas(areas);
    this.triggerAutosave();
  }

  deleteAreas(areas: Areas): void {
    this.decoree.deleteAreas(areas);
    this.triggerAutosave();
  }

  //
  // Obstacles
  //

  getObstacle(id: string): Obstacle | undefined {
    return this.decoree.getObstacle(id);
  }

  async addObstacles(obstacles: Obstacle[]): Promise<void> {
    await this.decoree.addObstacles(obstacles);
    this.triggerAutosave();
  }

  updateObstacle(obstacle: Obstacle): void {
    this.decoree.updateObstacle(obstacle);
    this.triggerAutosave();
  }

  updateObstacles(obstacles: Obstacle[]): void {
    this.decoree.updateObstacles(obstacles);
    this.triggerAutosave();
  }

  deleteObstacles(obstacles: Obstacle[]): void {
    this.decoree.deleteObstacles(obstacles);
    this.triggerAutosave();
  }

  //
  // Inter Area Connections
  //

  getInterAreaConnection(id: string): InterAreaConnection | undefined {
    return this.decoree.getInterAreaConnection(id);
  }

  addInterAreaConnections(connections: InterAreaConnection[]): boolean {
    const success = this.decoree.addInterAreaConnections(connections);
    if (success) {
      this.triggerAutosave();
    }

    return success;
  }

  updateInterAreaConnections(connections: InterAreaConnection[]): void {
    this.decoree.updateInterAreaConnections(connections);
    this.triggerAutosave();
  }

  deleteInterAreaConnections(connections: InterAreaConnection[]): void {
    this.decoree.deleteInterAreaConnections(connections);
    this.triggerAutosave();
  }

  //
  // Logical Spaces
  //

  getLogicalSpace(id: string): LogicalSpace | undefined {
    return this.decoree.getLogicalSpace(id);
  }

  async addLogicalSpaces(logicalSpaces: LogicalSpace[]): Promise<void> {
    await this.decoree.addLogicalSpaces(logicalSpaces);
    this.triggerAutosave();
  }

  updateLogicalSpace(logicalSpace: LogicalSpace): void {
    this.decoree.updateLogicalSpace(logicalSpace);
    this.triggerAutosave();
  }

  deleteLogicalSpaces(logicalSpaces: LogicalSpace[]): void {
    this.decoree.deleteLogicalSpaces(logicalSpaces);
    this.triggerAutosave();
  }

  //
  //  Templates
  //

  getTemplate(id: string): Template | undefined {
    return this.decoree.getTemplate(id);
  }

  addTemplates(templates: Templates): void {
    this.decoree.addTemplates(templates);
    this.triggerAutosave();
  }

  deleteTemplates(templates: Templates): void {
    this.decoree.deleteTemplates(templates);
    this.triggerAutosave();
  }

  async addTemplate(
    template: Template,
    areas: Areas,
    interAreaConnections: InterAreaConnection[],
    flowLayer: FlowLayer,
  ): Promise<void> {
    await this.decoree.addTemplate(template, areas, interAreaConnections, flowLayer);
    this.triggerAutosave();
  }

  updateTemplate(template: Template): void {
    this.decoree.updateTemplate(template);
    this.triggerAutosave();
  }

  updateTemplates(templates: Templates): void {
    this.decoree.updateTemplates(templates);
    this.triggerAutosave();
  }

  async regenerateTemplate(
    template: Template,
    areas: Areas,
    interAreaConnections: InterAreaConnection[],
    flowLayer: FlowLayer,
    previousAreas: Areas,
  ): Promise<void> {
    await this.decoree.regenerateTemplate(
      template,
      areas,
      interAreaConnections,
      flowLayer,
      previousAreas,
    );
    this.triggerAutosave();
  }

  //
  // Groups
  //

  getGroup(id: string): Group | undefined {
    return this.decoree.getGroup(id);
  }

  addGroups(groups: Group[]): void {
    this.decoree.addGroups(groups);
    this.triggerAutosave();
  }

  updateGroup(group: Group): void {
    this.decoree.updateGroup(group);
    this.triggerAutosave();
  }

  deleteGroups(groups: Group[]): void {
    this.decoree.deleteGroups(groups);
    this.triggerAutosave();
  }

  ungroupGroups(groups: Group[]): void {
    this.decoree.ungroupGroups(groups);
    this.triggerAutosave();
  }

  //
  // Jit Routing
  //

  getJitRoutingConfig(id: string): JustInTimeRoutingConfig | undefined {
    return this.decoree.getJitRoutingConfig(id);
  }

  addJitRoutingConfigs(jitRoutingPoints: JustInTimeRoutingConfig[]): void {
    this.decoree.addJitRoutingConfigs(jitRoutingPoints);
    this.triggerAutosave();
  }

  updateJitRoutingConfig(jitRoutingPoint: JustInTimeRoutingConfig): void {
    this.decoree.updateJitRoutingConfig(jitRoutingPoint);
    this.triggerAutosave();
  }

  deleteJitRoutingConfigs(jitRoutingConfigs: JustInTimeRoutingConfig[]): void {
    this.decoree.deleteJitRoutingConfigs(jitRoutingConfigs);
    this.triggerAutosave();
  }

  //
  // Flows
  //

  getFlow(id: string): Flow | undefined {
    return this.decoree.getFlow(id);
  }

  getFlowsByAreaId(areaId: string): Flow[] | undefined {
    return this.decoree.getFlowsByAreaId(areaId);
  }

  addFlows(flows: Flow[]): void {
    this.decoree.addFlows(flows);
    this.triggerAutosave();
  }

  updateFlow(flow: Flow): void {
    this.decoree.updateFlow(flow);
    this.triggerAutosave();
  }

  updateFlows(flows: Flow[]): void {
    this.decoree.updateFlows(flows);
    this.triggerAutosave();
  }

  deleteFlows(flows: Flow[]): void {
    this.decoree.deleteFlows(flows);
    this.triggerAutosave();
  }

  //
  // FlowStops
  //

  getFlowStop(id: string): FlowStop | undefined {
    return this.decoree.getFlowStop(id);
  }

  getFlowStopByAreaId(areaId: string): FlowStop | undefined {
    return this.decoree.getFlowStopByAreaId(areaId);
  }

  addFlowStops(flowStops: FlowStop[]): void {
    this.decoree.addFlowStops(flowStops);
    this.triggerAutosave();
  }

  updateFlowStop(flowStop: FlowStop): void {
    this.decoree.updateFlowStop(flowStop);
    this.triggerAutosave();
  }

  deleteFlowStops(flowStops: FlowStop[]): void {
    this.decoree.deleteFlowStops(flowStops);
    this.triggerAutosave();
  }

  //
  // Vehicles
  //

  getVehicleType(id: string): VehicleType | undefined {
    return this.decoree.getVehicleType(id);
  }

  addVehicleTypes(vehicleTypes: VehicleType[]): void {
    this.decoree.addVehicleTypes(vehicleTypes);
    this.triggerAutosave();
  }

  addVehiclesToHighways(vehicleTypes: FloorplanRevision['setup']['vehicleTypes']): void {
    this.decoree.addVehiclesToHighways(vehicleTypes);
    this.triggerAutosave();
  }

  updateVehicleType(vehicleType: VehicleType): void {
    this.decoree.updateVehicleType(vehicleType);
    this.triggerAutosave();
  }

  deleteVehicleTypes(vehicleTypes: VehicleType[]): void {
    this.decoree.deleteVehicleTypes(vehicleTypes);
    this.triggerAutosave();
  }

  //
  // Load Carriers
  //

  getLoadType(id: string): LoadType | undefined {
    return this.decoree.getLoadType(id);
  }

  addLoadTypes(loadTypes: LoadType[]): void {
    this.decoree.addLoadTypes(loadTypes);
    this.triggerAutosave();
  }

  deleteLoadTypes(loadTypes: LoadType[]): void {
    this.decoree.deleteLoadTypes(loadTypes);
    this.triggerAutosave();
  }

  //
  // Catalog
  //

  setCatalogTrafficRules(trafficRules: TrafficRulesCatalog): void {
    this.decoree.setCatalogTrafficRules(trafficRules);
    this.triggerAutosave();
  }

  getAllFileReferences(): FileReference[] {
    return this.decoree.getAllFileReferences();
  }

  getFileReferences(type: FileReference['type']): FileReference[] {
    return this.decoree.getFileReferences(type);
  }

  addFileReference(reference: FileReference): void {
    this.decoree.addFileReference(reference);
    this.triggerAutosave();
  }

  deleteFileReference(reference: FileReference): void {
    this.decoree.deleteFileReference(reference);
    this.triggerAutosave();
  }

  //
  // Floorplan
  //

  getFloorplan(): Floorplan | undefined {
    return this.decoree.getFloorplan();
  }

  setFloorplan(floorplan: Floorplan): void {
    this.decoree.setFloorplan(floorplan);
  }

  //
  // Tracks
  //

  getTracks(): Track[] | undefined {
    return this.decoree.getTracks();
  }

  setTracks(tracks: Track[]): void {
    this.decoree.setTracks(tracks);
    // this.triggerAutosave();
  }

  //
  // Selection
  //

  deleteSelection(selectedIds: string[]): void {
    try {
      this.decoree.deleteSelection(selectedIds);
    } finally {
      this.triggerAutosave();
    }
  }

  mirrorSelectionHorizontally(selectedIds: string[]): void {
    try {
      this.decoree.mirrorSelectionHorizontally(selectedIds);
    } finally {
      this.triggerAutosave();
    }
  }

  mirrorSelectionVertically(selectedIds: string[]): void {
    try {
      this.decoree.mirrorSelectionVertically(selectedIds);
    } finally {
      this.triggerAutosave();
    }
  }

  rotateSelection90Degrees(selectedIds: string[]): void {
    try {
      this.decoree.rotateSelection90Degrees(selectedIds);
    } finally {
      this.triggerAutosave();
    }
  }

  changeName(id: string, newName: string): boolean {
    const result = this.decoree.changeName(id, newName);
    this.triggerAutosave();
    return result;
  }

  //
  // Preferences
  //
  getPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
  ): FddmPreferenceValues[PreferenceKey] {
    return this.decoree.getPreference(key);
  }

  setPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
    value: FddmPreferenceValues[PreferenceKey],
  ): void {
    this.decoree.setPreference(key, value);
    this.triggerAutosave();
  }

  resetPreferences(): void {
    this.decoree.resetPreferences();
    this.triggerAutosave();
  }

  disposeState(): void {
    this.autosaveQueued = false;
    this.decoree.disposeState();
  }

  // private utility methods

  /**
   * Logical gatekeeper for autosaving
   */
  triggerAutosave(): void {
    const isEnabled = this.appStore.getState().autosaveEnabled;

    if (!isEnabled) return;

    this.autosaveQueued = true;
    this.processAutosaveQueue();
  }

  private processAutosaveQueue(): void {
    if (this.autosaveInProgress || !this.autosaveQueued) return;

    if (!this.appStore.getState().autosaveEnabled) {
      this.autosaveQueued = false;
      return;
    }

    this.autosaveQueued = false;
    this.autosaveInProgress = true;
    this.runQueuedAutosave();
  }

  private async runQueuedAutosave(): Promise<void> {
    try {
      await this.autosave();
    } finally {
      this.autosaveInProgress = false;
      this.processAutosaveQueue();
    }
  }

  private async autosave(): Promise<void> {
    try {
      const model = this.decoree.getModel();

      if (!model.metaData.revisionId || model.metaData.version === undefined) {
        logger.error('Cannot autosave a revision without a revision id and version.');
        return;
      }

      const autosaveContext: AutosaveContext = {
        projectId: model.metaData.projectId,
        alternativeId: model.metaData.alternativeId,
        revisionId: model.metaData.revisionId,
        revisionState: model.metaData.state,
      };

      const activeAlternative = this.appMetaStore.getState().activeLayoutAlternative;
      if (activeAlternative.status !== 'loaded') {
        throw Error('Expected activeLayoutAlternative to be loaded.');
      }

      /* 
       Only autosave if the current loaded revision is the latest one in the layoutAlternative
      */
      if (activeAlternative.data.lastRevisionId !== model.metaData.revisionId) {
        return;
      }

      const canvas = this.canvasStore.getState().instance;
      if (!canvas) {
        logger.error('Expected canvas instance to be defined');
        return;
      }

      const thumbnailBase64 = canvas.createTopViewThumbnail(
        THUMBNAIL_CONFIG.WIDTH,
        THUMBNAIL_CONFIG.HEIGHT,
        buildTagMatchRegExp([LayerNames.ORIGIN]),
      );
      this.fddmStore.getState().setThumbnail(thumbnailBase64);

      const savedRevisionMetaData = await this.projectServiceConnector.saveFloorplanRevisionDraft({
        projectId: model.metaData.projectId,
        alternativeId: model.metaData.alternativeId,
        revision: {
          ...model,
          uxonly: {
            ...model.uxonly,
            thumbnail: thumbnailBase64,
          },
          floorplan: { stations: [], vehicleLayouts: [] }, // Don't persist floorplan part of FDDM
          tracks: [], // Don't persist tracks
        },
      });

      if (!this.autosaveContextIsCurrent(autosaveContext)) return;

      /* 
        When going from a FINAL revision to a new DRAFT revision -> 
        Sync saved revision data to local activeLayoutAlternative state
        without fetching and loading the actual layoutAlternative from the server over again.
      */
      if (autosaveContext.revisionState === 'FINAL') {
        if (
          !savedRevisionMetaData.revisionId ||
          savedRevisionMetaData.version === undefined ||
          savedRevisionMetaData.projectId !== autosaveContext.projectId ||
          savedRevisionMetaData.alternativeId !== autosaveContext.alternativeId
        ) {
          logger.error('Autosave returned invalid revision metadata.', savedRevisionMetaData);
          return;
        }

        /* 
          Sync possibly updated metaData to local state (version, state) without needlessly overwriting the whole state
        */
        this.decoree.setMetaData({
          ...this.decoree.getMetaData(),
          ...savedRevisionMetaData,
        });

        this.appMetaStore.getState().setActiveLayoutAlternative({
          /* 
            Properties expected to be changed after finalizing and starting a new version
          */
          lastRevisionId: savedRevisionMetaData.revisionId,
          lastVersion: savedRevisionMetaData.version,
          lastModified: savedRevisionMetaData.lastUpdated,
          /* 
            Other properties, consistent with previous revisions
          */
          alternativeId: activeAlternative.data.alternativeId,
          name: activeAlternative.data.name,
          created: activeAlternative.data.created,
          lastModifiedBy: activeAlternative.data.lastModifiedBy,
          projectId: activeAlternative.data.projectId,
          lastThumbnail: activeAlternative.data.lastThumbnail,
        });

        replaceURLPath(
          `/${savedRevisionMetaData.projectId}/builder/${savedRevisionMetaData.alternativeId}/version/${savedRevisionMetaData.revisionId}`,
        );
      }
    } catch (error) {
      logger.error('autosave failed. ', error);

      // TODO: user notification ?
    }
  }

  private autosaveContextIsCurrent(context: AutosaveContext): boolean {
    const currentMetaData = this.decoree.getMetaData();

    if (
      currentMetaData.projectId !== context.projectId ||
      currentMetaData.alternativeId !== context.alternativeId ||
      currentMetaData.revisionId !== context.revisionId
    ) {
      return false;
    }

    const activeAlternative = this.appMetaStore.getState().activeLayoutAlternative;

    if (activeAlternative.status !== 'loaded') return false;

    return (
      activeAlternative.data.projectId === context.projectId &&
      activeAlternative.data.alternativeId === context.alternativeId
    );
  }
}
